import React, { useEffect, useRef, useState } from 'react';

import { ScrollPanel } from 'primereact/scrollpanel';
import {
  Coordinate,
  TaskCoordinate,
  TaskStatusTransitionCoordinate
} from 'types/interfaces';

type UnavailableItem =
  | Coordinate
  | TaskCoordinate
  | TaskStatusTransitionCoordinate;

type PositionType = 'top' | 'bottom';
type HorizontalPositionType = 'left' | 'right' | 'center';

interface UnavailableCoordinatesListProps {
  expiredCoordinates: UnavailableItem[];
  renderItem?: (item: UnavailableItem) => React.ReactNode;
  title?: string;
  maxVisibleItems?: number;
  verticalPosition?: PositionType;
  horizontalPosition?: HorizontalPositionType;
  verticalMargin?: number;
  horizontalMargin?: number;
  width?: number | string;
  inline?: boolean;
}

const UnavailableCoordinatesList: React.FC<UnavailableCoordinatesListProps> = ({
  expiredCoordinates,
  renderItem,
  title = 'Не удалось получить координаты:',
  maxVisibleItems = 5,
  verticalPosition = 'bottom',
  horizontalPosition = 'right',
  verticalMargin = 20,
  horizontalMargin = 20,
  width = 300,
  inline = false
}) => {
  const [maxHeight, setMaxHeight] = useState<string>('auto');
  const itemsRef = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    const calculateHeight = () => {
      if (itemsRef.current.length >= maxVisibleItems) {
        const heights = itemsRef.current
          .slice(0, maxVisibleItems)
          .map((el) => el?.offsetHeight || 40);
        const totalHeight = heights.reduce((acc, height) => acc + height, 0);
        setMaxHeight(`${totalHeight}px`);
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => window.removeEventListener('resize', calculateHeight);
  }, [expiredCoordinates, maxVisibleItems]);

  const defaultRenderItem = (item: UnavailableItem) => {
    if ('fromStatus' in item && 'toStatus' in item) {
      return `${item.toStatus?.label || 'Неизвестный статус'} — координаты отсутствуют`;
    }
    if ('name' in item && item.coordinateType !== 'task') {
      return item.name || 'Неизвестный монтажник';
    }
    return `Наряд ${(item as TaskCoordinate).id}`;
  };

  const renderFunction = renderItem || defaultRenderItem;

  const positionStyles = inline
    ? ({
        width: typeof width === 'number' ? `${width}px` : width,
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
      } as React.CSSProperties)
    : ({
        position: 'absolute',
        [verticalPosition]: `${verticalMargin}px`,
        width: typeof width === 'number' ? `${width}px` : width,
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        zIndex: 1000
      } as React.CSSProperties);

  if (!inline) {
    if (horizontalPosition === 'left') {
      positionStyles.left = `${horizontalMargin}px`;
    } else if (horizontalPosition === 'right') {
      positionStyles.right = `${horizontalMargin}px`;
    } else {
      positionStyles.left = '50%';
      positionStyles.transform = 'translateX(-50%)';
    }
  }

  return (
    <div style={positionStyles}>
      <h4
        style={{
          padding: '10px',
          margin: 0,
          borderBottom: '1px solid #ddd',
          fontSize: '14px',
          fontWeight: 500
        }}
      >
        {title}
      </h4>

      <ScrollPanel
        style={{
          width: '100%',
          height: maxHeight
        }}
      >
        <ul
          style={{
            listStyleType: 'none',
            padding: 0,
            margin: 0
          }}
        >
          {expiredCoordinates.map((item, index) => (
            <li
              key={index}
              ref={(el) => (itemsRef.current[index] = el)}
              style={{
                padding: '8px 10px',
                borderBottom: '1px solid #eee',
                fontSize: '13px',
                boxSizing: 'border-box',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            >
              {renderFunction(item)}
            </li>
          ))}
        </ul>
      </ScrollPanel>
    </div>
  );
};

export default UnavailableCoordinatesList;
